import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  LayoutGrid, Phone, PhoneCall, CalendarClock, Users, BarChart3,
  Sun, Moon, Bot, ChevronDown, MessageSquare, Megaphone, AlertTriangle, LogOut,
  PhoneIncoming, PhoneOff, PhoneOutgoing, ListChecks, Coffee, Utensils, Smile,
  GraduationCap, Briefcase, Target, Eye, Loader2 as Loader2Icon, Pause, WifiOff,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
  DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent, DropdownMenuPortal,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useTenant } from "@/contexts/TenantContext";
import { useConnection } from "@/contexts/ConnectionContext";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import api from "@/lib/api";
import Dialer from "@/components/Dialer";
import Softphone from "@/components/Softphone";
import ScreenRecorderController from "@/components/ScreenRecorderController";

import TeamChatPopover from "@/components/TeamChat";
import { channelMeta, hexToHslTriplet } from "@/lib/app-utils";
import { toast } from "sonner";

const C7_SMALL_LOGO = "/c7-mark.png";
const CONNECT7_WORDMARK = "/connect7-wordmark.png";

function useBrandWordmark() {
  const { tenant } = useTenant();
  return tenant?.branding?.logo_wordmark_url || CONNECT7_WORDMARK;
}

const NAV_PRIMARY = [
  { to: "/workspace", label: "Workspace", icon: LayoutGrid },
  { to: "/supervisor", label: "Supervisor Floor", icon: Eye, supervisorOnly: true },
  { to: "/orchestration", label: "AI Orchestration", icon: Bot, supervisorOnly: true },
];

const NAV_CALLS = [
  { to: "/calls/queue",     label: "Calls in queue",     icon: PhoneIncoming, badgeKey: "queue_count" },
  { to: "/calls/dropped",   label: "Dropped calls",      icon: PhoneOff,      badgeKey: "dropped_today" },
  { to: "/calls/callbacks", label: "Scheduled callbacks", icon: CalendarClock, badgeKey: "scheduled_callbacks" },
  { to: "/calls/logs",      label: "Call logs",          icon: ListChecks },
];

const NAV_OTHER = [
  { to: "/contacts",  label: "Contacts",  icon: Users },
  { to: "/analytics", label: "Analytics", icon: BarChart3 },
];

const BREAK_OPTIONS = [
  { k: "Tea break", I: Coffee },
  { k: "Lunch break", I: Utensils },
  { k: "Bio break", I: Smile },
];

const STATUS_COLOR = {
  "Available": "bg-emerald-500",
  "Manual Dial": "bg-orange-500",
  "Meeting": "bg-violet-500",
  "Training": "bg-sky-500",
};

function fmtSecs(s) {
  s = Math.max(0, Math.floor(s));
  const m = Math.floor(s / 60);
  const sec = s % 60;
  if (m >= 60) {
    const h = Math.floor(m / 60); const mr = m % 60;
    return `${h}h ${mr}m`;
  }
  return `${m}m ${sec.toString().padStart(2, "0")}s`;
}

export default function Layout() {
  const { user, logout } = useAuth();
  // Issue #2 — block sign-out while a call is live or a disposition is still
  // pending. The Softphone maintains window.__c7_callBusy / __c7_wrapPending.
  function guardedLogout() {
    if (typeof window !== "undefined" && window.__c7_callBusy) {
      if (window.__c7_wrapPending) {
        toast.error("Finish wrap-up & disposition before signing out", {
          description: "Pick a disposition to release the call.",
        });
        try { window.dispatchEvent(new CustomEvent("workspace:open-wrap")); } catch {}
      } else {
        toast.error("Hang up and disposition the active call before signing out");
      }
      return;
    }
    logout();
  }
  const { theme, toggle } = useTheme();
  const { tenant } = useTenant();
  const { online: backendOnline, reason: connReason, sessionInterrupted } = useConnection();
  const copilotEnabled = tenant?.settings?.ai_copilot_enabled !== false;
  const brandLogo = useBrandWordmark();

  // Unified brand theming — the agent workspace now shares the SAME premium
  // primary (default dark-blue #1E40AF) as the Login + Admin surfaces, sourced
  // from the tenant branding field so one control themes all three. Red is kept
  // strictly semantic (via `--destructive` + explicit `red-*` classes) for live
  // calls, hangup and alerts, so those are unaffected by this override.
  useEffect(() => {
    const hsl = hexToHslTriplet(tenant?.branding?.admin_button_color || "#1E40AF");
    const root = document.documentElement;
    if (hsl) { root.style.setProperty("--primary", hsl); root.style.setProperty("--ring", hsl); }
    return () => { root.style.removeProperty("--primary"); root.style.removeProperty("--ring"); };
  }, [tenant]);
  const nav = useNavigate();
  const location = useLocation();
  const [kpis, setKpis] = useState({});
  // Default presence is "Offline" — agent must explicitly go Ready by either
  // joining a campaign (auto-flip via Slice 3.2) or clicking the presence
  // pill. Backend resets agents.presence = "offline" on every login.
  const [presence, setPresence] = useState("Offline");
  // Pause tracking — EVERY non-Available/Offline state counts (breaks,
  // Manual Dial, Meeting, Training). `pausedStart` is the ms timestamp the
  // CURRENT paused state began; `pausedLabel` its display name; `pausedTotals`
  // accumulates seconds PER state for the breakdown popover.
  const [pausedStart, setPausedStart] = useState(null); // ms
  const [pausedLabel, setPausedLabel] = useState(null); // current paused state
  const [pausedTotals, setPausedTotals] = useState({}); // { [state]: seconds }
  const [tick, setTick] = useState(0);
  const [aiCopilot, setAiCopilot] = useState(true);
  const [dialerOpen, setDialerOpen] = useState(false);
  const [queueOpen, setQueueOpen] = useState(false);
  const [queue, setQueue] = useState({ calls: [], chats: [], emails: [], voicemails: [] });
  const { flags: featureFlags, connected: liveConnected } = useFeatureFlags();
  // Slice 3.2 — campaign opt-in (multi-select). Allowed list comes from the
  // user's groups' `allowed_campaigns`; active list is the agent's join set.
  const [allowedCampaigns, setAllowedCampaigns] = useState([]);
  const [activeCampaignIds, setActiveCampaignIds] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  // Slice 3.2b — inbound-queue opt-in (auto-joined from campaign linked_queues)
  const [availableQueues, setAvailableQueues] = useState([]);
  const [activeQueueIds, setActiveQueueIds] = useState([]);
  const [queuesLoading, setQueuesLoading] = useState(false);
  // Slice 3.4 — effective channel mode resolved by the backend from the
  // union of active campaigns. `off_hook` means the workspace should
  // park a persistent ConfBridge leg (predictive-dialer ready).
  const [channelMode, setChannelMode] = useState("on_hook");

  async function reloadQueues() {
    try {
      const r = await api.get("/me/queues");
      const list = Array.isArray(r.data) ? r.data : [];
      setAvailableQueues(list);
      setActiveQueueIds(list.filter((q) => q.joined).map((q) => q.id));
    } catch { /* silent */ }
  }

  // Shared KPI fetcher — used by the 30s poller AND by ad-hoc refresh
  // calls (e.g., right after activating a campaign so the topbar's
  // "Active leads" count updates immediately instead of waiting up to
  // 30 seconds for the next poll cycle).
  async function refreshKpis() {
    try {
      const r = await api.get("/dashboard/kpis");
      const d = r.data || {};
      setKpis((prev) => ({ ...prev, ...d }));
    } catch { /* silent */ }
  }

  // Slice 17 — live waiting-queue + dropped-call counts for the sidebar
  // badges + toast notifications. Polled faster than the 30s KPI cycle so
  // agents notice a new waiting call / dropped call quickly.
  const callCountsRef = useRef({ queue_count: 0, dropped_today: 0, init: false });
  async function refreshCallCounts() {
    try {
      const r = await api.get("/me/call-counts");
      const d = r.data || {};
      const prev = callCountsRef.current;
      setKpis((p) => ({ ...p, queue_count: d.queue_count, dropped_today: d.dropped_today, scheduled_callbacks: d.scheduled_callbacks }));
      if (prev.init) {
        if ((d.queue_count || 0) > (prev.queue_count || 0)) {
          toast.info(`${d.queue_count} call${d.queue_count > 1 ? "s" : ""} waiting in queue`, {
            description: "Open Calls in queue to grab a call.",
          });
        }
        if ((d.dropped_today || 0) > (prev.dropped_today || 0)) {
          toast.warning(`${d.dropped_today} dropped call${d.dropped_today > 1 ? "s" : ""} to call back`, {
            description: "Open Dropped calls to dial them back.",
          });
        }
      }
      callCountsRef.current = { queue_count: d.queue_count || 0, dropped_today: d.dropped_today || 0, init: true };
    } catch { /* silent */ }
  }

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get("/me/campaigns").then((r) => r.data).catch(() => []),
      api.get("/me/campaigns/active").then((r) => r.data).catch(() => []),
    ]).then(([all, active]) => {
      if (!alive) return;
      setAllowedCampaigns(Array.isArray(all) ? all : []);
      setActiveCampaignIds((Array.isArray(active) ? active : []).map((c) => c.id));
    });
    reloadQueues();
    // Slice 3.4 — fetch initial channel mode (in case the agent is mid-shift
    // on a page refresh and the backend already resolved off_hook for them)
    api.get("/me/conf-bridge")
      .then((r) => { if (alive && r.data?.channel_mode) setChannelMode(r.data.channel_mode); })
      .catch(() => { /* agent_license missing or no extension — fine */ });
    return () => { alive = false; };
  }, []);

  async function toggleCampaign(campaignId) {
    // Slice 16.3 — single-campaign selection: picking a campaign REPLACES any
    // current one; picking the active one again clears it. (Inbound queues can
    // still be many — that's handled separately by the queue opt-in.)
    const next = activeCampaignIds.includes(campaignId) ? [] : [campaignId];
    // Block campaign changes while a call is live OR a disposition/wrap-up is
    // still pending (window.__c7_callBusy is maintained by the Softphone and
    // covers live call + pending wrap; ringing is the pre-answer state).
    if (window.__c7_callBusy || phoneActivity.ringing || phoneActivity.inCall || dialerInCall) {
      toast.error("Can't change campaigns during a call", {
        description: window.__c7_wrapPending
          ? "Save your disposition / wrap-up first."
          : "Finish the current call first.",
      });
      return;
    }
    setCampaignsLoading(true);
    try {
      const r = await api.post("/me/campaigns/active", { campaign_ids: next });
      setActiveCampaignIds((r.data?.active_campaigns || []).map((c) => c.id));
      // Slice 16.3 — tell the softphone its dialing campaign changed.
      try { window.dispatchEvent(new CustomEvent("c7:campaign-changed")); } catch {}
      if (r.data?.presence === "available" && presence !== "Available") {
        setPresence("Available");
      }
      // Slice 3.4 — backend resolved effective channel mode for us
      if (r.data?.channel_mode) {
        setChannelMode(r.data.channel_mode);
      }
      // Campaign cascade auto-joined / pruned queues — refresh
      await reloadQueues();
      // Refresh topbar KPIs (Active leads, login hours, AHT, …) NOW so
      // the agent doesn't have to wait up to 30 seconds for the next
      // poll cycle to see the new "Active leads" count after activating
      // a campaign. Small extra call but huge UX win.
      refreshKpis();
      // Pause codes are scoped by active campaigns — refetch the list
      // so the presence dropdown only shows codes this campaign permits.
      refreshPauseCodes();
    } catch (e) {
      // 409 (on a call / wrap pending) or 429 (cooldown) → surface the reason.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409 || status === 429) {
        toast.error(status === 429 ? "Slow down" : "Can't change campaigns now", {
          description: detail || "Please try again in a moment.",
        });
      } else if (status === 403) {
        toast.error("Not allowed", { description: detail || "You can't join that campaign." });
      } else if (detail) {
        toast.error(detail);
      }
      // revert UI to the server truth
      try {
        const cur = await api.get("/me/campaigns/active");
        setActiveCampaignIds((cur.data || []).map((c) => c.id));
      } catch { /* ignore */ }
    } finally {
      setCampaignsLoading(false);
    }
  }

  async function toggleQueue(queueId) {
    const next = activeQueueIds.includes(queueId)
      ? activeQueueIds.filter((x) => x !== queueId)
      : [...activeQueueIds, queueId];
    setQueuesLoading(true);
    try {
      const r = await api.post("/me/queues/active", { queue_ids: next });
      setActiveQueueIds((r.data?.active_queues || []).map((q) => q.id));
    } catch {
      // revert silently
    } finally {
      setQueuesLoading(false);
    }
  }

  useEffect(() => {
    let alive = true;
    const fetchKpis = () => {
      if (!alive) return;
      refreshKpis();
    };
    fetchKpis();
    const id = setInterval(fetchKpis, 30000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  // Live call-count poller (waiting queue + dropped) — drives sidebar badges
  // + toast alerts. 6s cadence so a new waiting/dropped call surfaces fast.
  useEffect(() => {
    let alive = true;
    const tickCounts = () => { if (alive) refreshCallCounts(); };
    tickCounts();
    const id = setInterval(tickCounts, 6000);
    const onRefresh = () => refreshCallCounts();
    window.addEventListener("c7:call-counts-refresh", onRefresh);
    return () => { alive = false; clearInterval(id); window.removeEventListener("c7:call-counts-refresh", onRefresh); };
  }, []);

  // Lock the sidebar navigation while a call is ringing / live. Agents may
  // navigate ONLY when paused or offline (and not on a call) — the softphone
  // emits `c7:call-activity`; ARI predictive/inbound calls fire dialer:bridged
  // /ended (those don't surface as a local SIP session).
  const [phoneActivity, setPhoneActivity] = useState({ ringing: false, inCall: false });
  const [dialerInCall, setDialerInCall] = useState(false);
  useEffect(() => {
    const onActivity = (e) => setPhoneActivity({
      ringing: !!e.detail?.ringing, inCall: !!e.detail?.inCall });
    const onBridged = () => setDialerInCall(true);
    const onEnded = () => setDialerInCall(false);
    window.addEventListener("c7:call-activity", onActivity);
    window.addEventListener("dialer:bridged", onBridged);
    window.addEventListener("dialer:ended", onEnded);
    return () => {
      window.removeEventListener("c7:call-activity", onActivity);
      window.removeEventListener("dialer:bridged", onBridged);
      window.removeEventListener("dialer:ended", onEnded);
    };
  }, []);

  // Live tick for the pause timer
  useEffect(() => {
    if (!pausedStart) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [pausedStart]);

  // Slice 3.5c — Dynamic pause codes from active campaigns. Replaces the
  // old hardcoded BREAK_OPTIONS (Tea/Lunch/Bio) with the tenant's own
  // catalog filtered by campaign's `allowed_pause_codes`. Refreshed each
  // time the agent's active campaigns change.
  const [pauseCodes, setPauseCodes] = useState([]);
  const refreshPauseCodes = async () => {
    try {
      const r = await api.get("/me/pause-codes");
      setPauseCodes(Array.isArray(r.data) ? r.data : []);
    } catch { setPauseCodes([]); }
  };
  useEffect(() => { refreshPauseCodes(); }, []);

  // Daily paused-time totals (persisted server-side across ALL sessions,
  // resets at midnight IST). `by_state` holds COMPLETED segments; the live
  // in-progress segment is ticked client-side from `current_since`.
  const refreshPauseSummary = async () => {
    try {
      const r = await api.get("/me/pause-summary");
      const d = r.data || {};
      setPausedTotals(d.by_state || {});
      if (d.current_label && d.current_since) {
        setPausedLabel(d.current_label);
        setPausedStart(Date.parse(d.current_since));
      } else {
        setPausedLabel(null);
        setPausedStart(null);
      }
      // Re-hydrate the presence pill from the server so a hard page reload
      // (or direct URL navigation) keeps the real state (e.g. Available) and
      // the Available-pinning redirect still fires. Skip while the agent is
      // ringing / on a live call so we never clobber the in-call pill.
      if (d.presence_display && !phoneActivity.inCall && !phoneActivity.ringing && !dialerInCall) {
        setPresence(d.presence_display);
      }
    } catch { /* silent */ }
  };
  useEffect(() => {
    refreshPauseSummary();
    const id = setInterval(refreshPauseSummary, 15000);
    return () => clearInterval(id);
  }, []);

  // Fixed top-level pause states (defined in code, not admin-editable). The
  // Break sub-list comes from the admin "Pause Codes" catalog. Everything that
  // is not Available/Offline counts as paused time.
  const FIXED_PAUSE = useMemo(() => ["Manual Dial", "Meeting", "Training"], []);
  const isPaused = (name) => !!name && name !== "Available" && name !== "Offline";
  // Break sub-list = admin pause codes, minus any that collide with a fixed
  // top-level label (so e.g. "Training" never appears twice).
  const breakCodes = useMemo(
    () => pauseCodes.filter((p) => !FIXED_PAUSE.includes(p.name) && p.name !== "Available"),
    [pauseCodes, FIXED_PAUSE],
  );

  async function changePresence(next) {
    // Optimistic timer for the state we're ENTERING (server is the source of
    // truth for completed totals — we refetch right after the POST).
    if (isPaused(next)) {
      setPausedStart(Date.now());
      setPausedLabel(next);
    } else {
      setPausedStart(null);
      setPausedLabel(null);
    }
    setPresence(next);
    // Slice 3.5l — persist to backend so the dialer pool sees the new state.
    // "available" releases the agent for predictive dials; every other state
    // (Manual Dial / Meeting / Training / any Break code) maps to "break" with
    // `reason=<display name>` so floor/supervisor reports show the human label.
    const apiPresence = (next === "Available")
      ? "available"
      : (next === "Offline" ? "offline"
      : (next === "Manual Dial" ? "manual" : "break"));
    try {
      await api.post("/me/presence", {
        presence: apiPresence,
        reason: (apiPresence === "break" || apiPresence === "manual") ? next : "",
      });
      // Server has now accumulated the leaving segment → sync the daily totals.
      refreshPauseSummary();
    } catch (e) {
      // Best-effort; UI state already updated.
      // eslint-disable-next-line no-console
      console.warn("[presence] persist failed", e);
    }
  }

  function openQueue() {
    setQueueOpen(true);
    api.get("/queue").then((r) => setQueue(r.data)).catch(() => {});
  }

  // Slice 15.1 — the softphone auto-switched us into Manual Dial when a manual
  // WebRTC call started. Reflect it on the pill WITHOUT re-POSTing (the backend
  // /me/manual-call already persisted presence=manual + agent_state=talking).
  useEffect(() => {
    function onSetPresence(e) {
      const display = e.detail?.display;
      if (!display) return;
      if (isPaused(display)) { setPausedStart(Date.now()); setPausedLabel(display); }
      else { setPausedStart(null); setPausedLabel(null); }
      setPresence(display);
      // The backend (/me/manual-call) already accumulated + set presence —
      // sync the persisted daily totals.
      refreshPauseSummary();
    }
    window.addEventListener("c7:set-presence", onSetPresence);
    return () => window.removeEventListener("c7:set-presence", onSetPresence);
  }, [pausedStart, pausedLabel]);

  const liveElapsed = pausedStart ? Math.floor((Date.now() - pausedStart) / 1000) : 0;
  const onBreak = !!pausedStart;
  // Per-state totals INCLUDING the live elapsed of the current paused state.
  const pausedBreakdown = useMemo(() => {
    const out = { ...pausedTotals };
    if (pausedLabel) out[pausedLabel] = (out[pausedLabel] || 0) + liveElapsed;
    return out;
  }, [pausedTotals, pausedLabel, liveElapsed]);
  const breakdownEntries = Object.entries(pausedBreakdown).filter(([, s]) => s > 0);
  const totalBreak = breakdownEntries.reduce((a, [, s]) => a + s, 0);

  // Sidebar lock: nav is enabled ONLY when the agent is paused/offline
  // (presence !== Available) AND not ringing / on a call.
  const onAnyCall = phoneActivity.inCall || dialerInCall;
  const navLocked = presence === "Available" || onAnyCall || phoneActivity.ringing;

  // Keep the agent pinned to the workspace whenever they are ready to receive
  // calls (Available) or on/ringing any call — even if they navigated to
  // Contacts / Call logs / etc. or placed a manual dial from the dialpad there.
  useEffect(() => {
    const mustBeOnWorkspace = presence === "Available" || onAnyCall || phoneActivity.ringing;
    if (mustBeOnWorkspace && location.pathname !== "/workspace") {
      nav("/workspace");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presence, onAnyCall, phoneActivity.ringing, location.pathname]);

  return (
    <TooltipProvider delayDuration={150}>
      <div className="min-h-screen flex flex-col bg-background text-foreground">
        {/* Connection banner — backend OR telephony (Asterisk/SIP) unreachable. */}
        {!backendOnline && (
          <div data-testid="connection-banner" className="flex items-center gap-3 px-4 sm:px-6 py-2.5 bg-red-50 dark:bg-red-500/10 border-b border-red-200 dark:border-red-500/30">
            <WifiOff className="w-4 h-4 text-red-600 dark:text-red-400 shrink-0" />
            <span className="text-xs sm:text-sm text-red-900 dark:text-red-200 truncate">
              {connReason === "telephony" ? (
                <><strong>Your phone line is offline.</strong> The telephony service is unreachable — calls are paused. Trying to reconnect…</>
              ) : (
                <><strong>Reconnecting to server…</strong> Calls are paused until the connection is restored.</>
              )}
            </span>
            <span className="ml-auto"><Loader2Icon className="w-4 h-4 text-red-500 animate-spin" /></span>
          </div>
        )}
        {backendOnline && sessionInterrupted && (
          <div data-testid="connection-recovering-banner" className="flex items-center gap-3 px-4 sm:px-6 py-2 bg-amber-50 dark:bg-amber-500/10 border-b border-amber-200 dark:border-amber-500/30">
            <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
            <span className="text-xs sm:text-sm text-amber-900 dark:text-amber-200 truncate">
              Connection restored — please sign out and back in to resume taking calls.
            </span>
          </div>
        )}
        {/* SLA banner — only render when backend reports at-risk customers */}
        {(kpis.sla_at_risk ?? 0) > 0 && (
          <div data-testid="sla-banner" className="flex items-center gap-3 px-4 sm:px-6 py-2.5 bg-amber-50 dark:bg-amber-500/10 border-b border-amber-200 dark:border-amber-500/30">
            <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
            <span className="text-xs sm:text-sm text-amber-900 dark:text-amber-200 truncate">
              <strong>{kpis.sla_at_risk} high-value {kpis.sla_at_risk === 1 ? "customer" : "customers"}</strong>
              {kpis.sla_longest_wait_seconds ? ` waiting >${Math.floor((kpis.sla_longest_wait_seconds || 0) / 60)} min${Math.floor((kpis.sla_longest_wait_seconds || 0) / 60) === 1 ? "" : "s"}` : " in queue"}
              {kpis.sla_breach_in_seconds ? ` — SLA breach in ${Math.max(1, Math.floor(kpis.sla_breach_in_seconds / 60))} min${Math.floor(kpis.sla_breach_in_seconds / 60) === 1 ? "" : "s"}` : ""}
            </span>
            <div className="ml-auto flex items-center gap-2">
              <Button data-testid="sla-view-queue-btn" onClick={openQueue} size="sm" className="h-8 rounded-full bg-secondary hover:bg-secondary/90 text-secondary-foreground gap-1.5">
                <ListChecks className="w-3.5 h-3.5" /> View queue
              </Button>
            </div>
          </div>
        )}

        {/* Top toolbar */}
        <header className="sticky top-0 z-30 bg-card/80 glass border-b border-border">
          <div className="flex items-center gap-2 px-4 sm:px-6 h-16">
            {/* Brand — fixed, never shrinks */}
            <NavLink to="/workspace" className="flex items-center shrink-0" data-testid="brand-link">
              <span className="inline-flex items-center dark:bg-white dark:rounded-md dark:px-2 dark:py-1">
                <img src={brandLogo} alt="Connect7 by Haloocom" className="h-9 w-auto max-w-[190px] object-contain" />
              </span>
            </NavLink>

            <div className="h-7 w-px bg-border shrink-0 hidden md:block" />

            {/* LEFT zone — agent state & routing controls. Shrinks + clips (never overlaps the right zone). */}
            <div className="flex items-center gap-1.5 min-w-0 overflow-hidden">
            {/* Presence with cascading menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="agent-status-dropdown" variant="outline" className="h-8 rounded-full px-2.5 gap-1.5 shrink-0 max-w-[140px]">
                  <span className={`w-2 h-2 rounded-full shrink-0 ${STATUS_COLOR[presence] || (isPaused(presence) ? "bg-amber-500" : "bg-slate-400")} ${onBreak ? "animate-pulse" : ""}`} />
                  <span className="text-sm truncate">{presence}</span>
                  <ChevronDown className="w-3.5 h-3.5 opacity-60 shrink-0" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem data-testid="presence-available" onClick={() => changePresence("Available")}>
                  <span className="w-2 h-2 rounded-full bg-emerald-500 mr-2" /> Available
                </DropdownMenuItem>
                {featureFlags?.allow_pause_self !== false && (<>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger data-testid="presence-break-trigger">
                    <Pause className="w-3.5 h-3.5 mr-2" /> Break
                  </DropdownMenuSubTrigger>
                  <DropdownMenuPortal>
                    <DropdownMenuSubContent>
                      {breakCodes.length === 0 && (
                        <div className="px-2 py-1.5 text-[11px] text-muted-foreground">
                          No break reasons configured. Add them in Admin → Pause Codes.
                        </div>
                      )}
                      {breakCodes.map((pc) => (
                        <DropdownMenuItem
                          key={pc.id}
                          data-testid={`presence-${(pc.name || pc.id).toLowerCase().replace(/\s+/g, "-")}`}
                          onClick={() => changePresence(pc.name)}
                        >
                          <span className="inline-block w-2 h-2 rounded-full mr-2" style={{ background: pc.color || "#94A3B8" }} />
                          {pc.name}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuSubContent>
                  </DropdownMenuPortal>
                </DropdownMenuSub>
                <DropdownMenuItem data-testid="presence-manual-dial" onClick={() => changePresence("Manual Dial")} style={{ display: featureFlags?.allow_manual_dial === false ? "none" : undefined }}>
                  <PhoneOutgoing className="w-3.5 h-3.5 mr-2" /> Manual Dial
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="presence-meeting" onClick={() => changePresence("Meeting")}>
                  <Briefcase className="w-3.5 h-3.5 mr-2" /> Meeting
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="presence-training" onClick={() => changePresence("Training")}>
                  <GraduationCap className="w-3.5 h-3.5 mr-2" /> Training
                </DropdownMenuItem>
                </>)}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Pause timer — combined total by default; click to see the
                per-state breakdown (Break codes, Manual Dial, Meeting, Training). */}
            {(onBreak || totalBreak > 0) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="break-timer"
                    title="Paused time"
                    className={`hidden lg:flex h-8 items-center gap-1.5 rounded-full border px-2 text-xs transition-colors shrink-0 ${onBreak ? "bg-amber-50 dark:bg-amber-500/10 border-amber-300 dark:border-amber-500/40 text-amber-800 dark:text-amber-200" : "bg-muted border-border text-muted-foreground"} hover:brightness-95`}
                  >
                    <Pause className="w-3.5 h-3.5 shrink-0" />
                    <span className="font-mono font-semibold">{fmtSecs(totalBreak)}</span>
                    {onBreak && <span className="relative inline-block w-1.5 h-1.5 rounded-full bg-amber-500 text-amber-500 live-pulse" />}
                    <ChevronDown className="w-3 h-3 opacity-50 shrink-0" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56" data-testid="break-timer-breakdown">
                  <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    Paused time by state
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {breakdownEntries
                    .sort((a, b) => b[1] - a[1])
                    .map(([name, secs]) => (
                      <div
                        key={name}
                        data-testid={`break-breakdown-${name.toLowerCase().replace(/\s+/g, "-")}`}
                        className="flex items-center justify-between px-2 py-1.5 text-sm"
                      >
                        <span className="flex items-center gap-2">
                          <span className={`inline-block w-1.5 h-1.5 rounded-full ${pausedLabel === name ? "bg-amber-500 animate-pulse" : "bg-slate-400"}`} />
                          {name}
                        </span>
                        <span className="font-mono text-xs">{fmtSecs(secs)}</span>
                      </div>
                    ))}
                  <DropdownMenuSeparator />
                  <div className="flex items-center justify-between px-2 py-1.5 text-sm font-semibold">
                    <span>Total</span>
                    <span className="font-mono text-xs" data-testid="break-breakdown-total">{fmtSecs(totalBreak)}</span>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            <div className="hidden md:flex items-center gap-1.5 min-w-0">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    data-testid="campaigns-btn"
                    className={`h-8 rounded-full gap-1.5 px-2.5 shrink-0 ${
                      activeCampaignIds.length > 0 && presence !== "Available"
                        ? "bg-amber-100 dark:bg-amber-500/15 text-amber-900 dark:text-amber-200 hover:bg-amber-200 dark:hover:bg-amber-500/25 border border-amber-300 dark:border-amber-500/40"
                        : "bg-secondary text-secondary-foreground hover:bg-secondary/90"
                    }`}
                  >
                    <Megaphone className="w-4 h-4 shrink-0" />
                    <span className="hidden xl:inline">Campaigns</span>
                    {activeCampaignIds.length > 0 && (
                      <Badge
                        variant={presence === "Available" ? "default" : "outline"}
                        className={`h-5 px-1.5 text-[10px] font-bold ${
                          presence !== "Available"
                            ? "bg-amber-200 dark:bg-amber-500/30 text-amber-900 dark:text-amber-100 border-amber-400"
                            : ""
                        }`}
                      >
                        {activeCampaignIds.length}
                      </Badge>
                    )}
                    <ChevronDown className="w-3.5 h-3.5 opacity-70 shrink-0" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="min-w-[280px]">
                  <DropdownMenuLabel className="flex items-center justify-between">
                    <span>Active Campaign <span className="font-normal text-[10px] text-muted-foreground">(one at a time)</span></span>
                    {campaignsLoading && <Loader2Icon className="w-3 h-3 animate-spin opacity-50" />}
                  </DropdownMenuLabel>
                  {activeCampaignIds.length > 0 && presence !== "Available" && (
                    <div className="mx-2 mb-1 px-2 py-1.5 text-[11px] rounded bg-amber-50 dark:bg-amber-500/10 text-amber-900 dark:text-amber-200 border border-amber-200 dark:border-amber-500/30">
                      Calls paused — you&apos;re on <strong>{presence}</strong>. Campaign membership is preserved; go Available to resume.
                    </div>
                  )}
                  {allowedCampaigns.length === 0 ? (
                    <div data-testid="campaigns-empty" className="px-2 py-3 text-xs text-muted-foreground">
                      No campaigns assigned. Ask your admin to add you to a campaign-enabled group.
                    </div>
                  ) : (
                    allowedCampaigns.map((c) => {
                      const active = activeCampaignIds.includes(c.id);
                      const lockSwitch = onAnyCall || phoneActivity.ringing;
                      return (
                        <DropdownMenuItem
                          key={c.id}
                          data-testid={`campaign-toggle-${c.id}`}
                          disabled={lockSwitch}
                          title={lockSwitch ? "Finish the current call before switching campaigns" : undefined}
                          onSelect={(e) => { e.preventDefault(); toggleCampaign(c.id); }}
                          className="flex items-center justify-between gap-3 cursor-pointer"
                        >
                          <span className="font-medium truncate">{c.name || c.label || c.id}</span>
                          <Switch checked={active} className="pointer-events-none scale-90" />
                        </DropdownMenuItem>
                      );
                    })
                  )}
                  {(onAnyCall || phoneActivity.ringing) && (
                    <div data-testid="campaign-locked-note" className="mx-2 my-1 px-2 py-1.5 text-[11px] rounded bg-muted text-muted-foreground border border-border">
                      Campaign switching is locked during an active call.
                    </div>
                  )}
                  {activeCampaignIds.length === 0 && allowedCampaigns.length > 0 && (
                    <>
                      <DropdownMenuSeparator />
                      <div className="px-2 py-2 text-[11px] text-amber-700 dark:text-amber-300">
                        Select a campaign to start receiving calls and to enable manual dialing.
                      </div>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Slice 3.2b — Inbound queue opt-in (auto-joined from campaigns) */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    data-testid="queues-btn"
                    className={`h-8 rounded-full gap-1.5 px-2.5 shrink-0 ${
                      activeQueueIds.length > 0 && presence !== "Available"
                        ? "bg-amber-100 dark:bg-amber-500/15 text-amber-900 dark:text-amber-200 hover:bg-amber-200 dark:hover:bg-amber-500/25 border border-amber-300 dark:border-amber-500/40"
                        : availableQueues.length === 0
                        ? "bg-muted text-muted-foreground hover:bg-muted/80"
                        : "bg-secondary text-secondary-foreground hover:bg-secondary/90"
                    }`}
                  >
                    <PhoneIncoming className="w-4 h-4 shrink-0" />
                    <span className="hidden xl:inline">Queues</span>
                    {activeQueueIds.length > 0 && (
                      <Badge
                        variant={presence === "Available" ? "default" : "outline"}
                        className={`h-5 px-1.5 text-[10px] font-bold ${
                          presence !== "Available"
                            ? "bg-amber-200 dark:bg-amber-500/30 text-amber-900 dark:text-amber-100 border-amber-400"
                            : ""
                        }`}
                      >
                        {activeQueueIds.length}
                      </Badge>
                    )}
                    <ChevronDown className="w-3.5 h-3.5 opacity-70 shrink-0" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="min-w-[300px]">
                  <DropdownMenuLabel className="flex items-center justify-between">
                    <span>Inbound Queues</span>
                    {queuesLoading && <Loader2Icon className="w-3 h-3 animate-spin opacity-50" />}
                  </DropdownMenuLabel>
                  {availableQueues.length === 0 ? (
                    <div data-testid="queues-empty" className="px-2 py-3 text-xs text-muted-foreground">
                      {activeCampaignIds.length === 0
                        ? "Join a campaign first — queues come from its linked queues."
                        : "Your active campaigns have no linked queues. Ask your admin to attach queues to the campaign."}
                    </div>
                  ) : (
                    availableQueues.map((q) => {
                      const active = activeQueueIds.includes(q.id);
                      return (
                        <DropdownMenuItem
                          key={q.id}
                          data-testid={`queue-toggle-${q.id}`}
                          onSelect={(e) => { e.preventDefault(); toggleQueue(q.id); }}
                          className="flex items-center justify-between gap-3 cursor-pointer"
                        >
                          <div className="flex flex-col min-w-0 flex-1">
                            <span className="font-medium truncate">{q.name || q.label || q.id}</span>
                            {q.strategy && (
                              <span className="text-[10px] text-muted-foreground capitalize">{q.strategy.replace(/_/g, " ")}</span>
                            )}
                          </div>
                          <Switch checked={active} className="pointer-events-none scale-90" />
                        </DropdownMenuItem>
                      );
                    })
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Active leads pill */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <div data-testid="active-leads-pill" className="hidden lg:flex h-8 items-center gap-1.5 rounded-full border border-border bg-card px-2 text-xs shrink-0">
                    <Target className="w-3.5 h-3.5 text-primary shrink-0" />
                    <span className="font-display font-bold text-sm">{kpis.active_leads ?? 0}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>Leads available to dial in your auto-dial campaign</TooltipContent>
              </Tooltip>

              {/* Slice 3.4 — Off-hook channel mode indicator. Visible only
                  when at least one joined campaign requires a persistent
                  ConfBridge leg (predictive / progressive / blended or an
                  explicit off_hook override). */}
              {channelMode === "off_hook" && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div
                      data-testid="off-hook-pill"
                      className="flex h-8 w-8 items-center justify-center rounded-full border border-amber-300 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/30 shrink-0"
                    >
                      <PhoneOutgoing className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    Persistent ConfBridge leg — predictive dialer will bridge customer calls in with zero ring latency.
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
            {/* end LEFT zone */}
            </div>

            {/* RIGHT zone — AI Copilot, KPIs, theme & agent. Fixed width, never shrinks. */}
            <div className="ml-auto flex items-center gap-1 sm:gap-2 shrink-0 pl-1.5">
              <div className="h-7 w-px bg-border shrink-0 hidden md:block" />
              {copilotEnabled && featureFlags && featureFlags.ai_copilot !== false ? (
                <div
                  data-testid="ai-copilot-toggle"
                  role="button"
                  tabIndex={0}
                  title="Copilot"
                  onClick={() => setAiCopilot((v) => !v)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setAiCopilot((v) => !v); }}
                  className={`hidden md:flex items-center gap-1.5 h-8 px-2 rounded-full border cursor-pointer select-none shrink-0 transition-colors ${aiCopilot ? "bg-primary/10 text-primary border-primary/40 dark:bg-sky-400/15 dark:text-sky-300 dark:border-sky-400/50" : "bg-card border-border hover:bg-muted text-muted-foreground"}`}
                >
                  <Bot className="w-4 h-4 shrink-0" />
                  <span className="text-sm whitespace-nowrap hidden 2xl:inline">Copilot</span>
                  <Switch checked={aiCopilot} onCheckedChange={setAiCopilot} className="pointer-events-none scale-90 data-[state=checked]:dark:bg-sky-400" />
                </div>
              ) : null}

              {/* KPIs */}
              <div className="hidden lg:flex items-center divide-x divide-border">
                {[
                  { k: "AHT", v: kpis.aht || "—" },
                  { k: "Calls", v: kpis.calls_handled ?? 0 },
                  { k: "Login Hrs", v: kpis.login_hours || "—" },
                ].map((x) => (
                  <div key={x.k} className="px-2 text-center shrink-0" data-testid={`kpi-${x.k.toLowerCase()}`}>
                    <div className="text-overline text-muted-foreground whitespace-nowrap">{x.k}</div>
                    <div className="font-display font-bold text-sm whitespace-nowrap">{x.v}</div>
                  </div>
                ))}
              </div>

            {/* Theme + agent */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button data-testid="theme-toggle" onClick={toggle} variant="ghost" size="icon" className="rounded-full">
                  {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{theme === "dark" ? "Switch to light" : "Switch to dark"}</TooltipContent>
            </Tooltip>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="agent-menu" className="flex items-center gap-2 px-2 rounded-full hover:bg-muted h-10">
                  <span className="relative">
                    <Avatar className="h-7 w-7"><AvatarFallback style={{ background: user?.avatar_color || "#0A195C", color: "white" }} className="text-[11px] font-bold">{user?.initials}</AvatarFallback></Avatar>
                    <span
                      data-testid="live-conn-pill"
                      title={liveConnected ? "Live sync: connected" : "Live sync: reconnecting…"}
                      className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-card ${liveConnected ? "bg-emerald-500" : "bg-muted-foreground/50"}`}
                    />
                  </span>
                  <div className="hidden sm:block text-left leading-tight min-w-0">
                    <div className="text-overline text-muted-foreground">Agent</div>
                    <div className="text-sm font-medium truncate max-w-[140px]">{user?.name}</div>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
                {user?.role && (
                  <div className="px-2 pb-1.5 text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                    Role: <span className="font-bold text-foreground">{user.role.replace(/_/g, " ")}</span>
                  </div>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="logout-btn" onClick={guardedLogout}><LogOut className="w-4 h-4 mr-2" /> Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Body: sidebar + outlet */}
        <div className="flex-1 flex">
          <aside className="w-16 sticky top-[88px] self-start h-[calc(100vh-88px-32px)] hidden md:flex flex-col items-center py-3 gap-2 border-r border-border bg-card/40 overflow-y-auto overflow-x-hidden">
            {NAV_PRIMARY.filter((n) => !n.supervisorOnly || ["supervisor", "admin", "super_admin", "super_super_admin"].includes(user?.role)).map((n) => <NavItem key={n.to} n={n} kpis={kpis} locked={navLocked && n.to !== "/workspace"} />)}
            <Divider />
            <SectionLabel>Calls</SectionLabel>
            {NAV_CALLS.filter((n) => {
              if (n.to === "/calls/queue") return featureFlags?.show_calls_in_queue !== false;
              if (n.to === "/calls/callbacks") return featureFlags?.show_callbacks !== false;
              if (n.to === "/calls/logs") return featureFlags?.show_call_logs !== false;
              return true;
            }).map((n) => <NavItem key={n.to} n={n} kpis={kpis} locked={navLocked} />)}
            <Divider />
            {NAV_OTHER.filter((n) => {
              if (n.to === "/contacts") return featureFlags?.show_contacts !== false;
              if (n.to === "/analytics") return featureFlags?.show_analytics !== false;
              return true;
            }).map((n) => <NavItem key={n.to} n={n} kpis={kpis} locked={navLocked} />)}
          </aside>

          <main className="flex-1 min-w-0">
            <Outlet context={{ kpis, aiCopilot: aiCopilot && copilotEnabled, openQueue, presence }} />
          </main>
        </div>

        <Dialer open={dialerOpen} onOpenChange={setDialerOpen} />
        <QueueDialog open={queueOpen} onOpenChange={setQueueOpen} queue={queue} onPick={() => { setQueueOpen(false); nav("/workspace"); }} />

        {/* Floating team chat — fixed bottom-left (z-50 so popover sits above softphone) */}
        {featureFlags?.show_team_chat !== false && (
          <div className="fixed bottom-6 left-3 z-50 hidden md:block">
            <TeamChatPopover />
          </div>
        )}

        {/* Softphone widget (Slice 5) — handles its own FAB + panel + WS */}
        <Softphone channelMode={channelMode} />

        <ScreenRecorderController presence={presence} />

        {/* Session-disconnected modal — connectivity dropped then returned. A
            softphone can't reliably rebuild its SIP/off-hook channel in place,
            so we require a clean re-login (a fresh page load re-registers and
            re-parks). Non-dismissable: the only action is to sign back in. */}
        <Dialog open={!!sessionInterrupted}>
          <DialogContent
            data-testid="session-disconnected-modal"
            className="sm:max-w-md [&>button.absolute]:hidden"
            onEscapeKeyDown={(e) => e.preventDefault()}
            onInteractOutside={(e) => e.preventDefault()}
            onPointerDownOutside={(e) => e.preventDefault()}
          >
            <DialogHeader>
              <div className="mx-auto mb-2 flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-500/10 text-amber-600 dark:text-amber-400">
                <AlertTriangle className="h-7 w-7" />
              </div>
              <DialogTitle className="text-center">Session disconnected</DialogTitle>
              <DialogDescription className="text-center">
                Your connection to the server dropped and your calling session
                needs to be re-established. Please sign out and sign back in to
                resume taking calls. Your login hours and call records are safe.
              </DialogDescription>
            </DialogHeader>
            <Button
              data-testid="session-disconnected-relogin-btn"
              className="mt-2 h-11 w-full rounded-full bg-secondary text-secondary-foreground hover:bg-secondary/90"
              onClick={() => logout()}
            >
              <LogOut className="w-4 h-4 mr-2" /> Log out & sign in again
            </Button>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}

function NavItem({ n, kpis, locked = false }) {
  const Icon = n.icon;
  const raw = n.badgeKey ? kpis[n.badgeKey] : null;
  const badgeVal = (typeof raw === "number" && raw > 0) ? raw : (raw && raw !== 0 ? raw : null);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="relative w-11 h-11">
          <NavLink
            to={n.to}
            data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
            aria-disabled={locked}
            onClick={(e) => { if (locked) e.preventDefault(); }}
            className={({ isActive }) =>
              `w-11 h-11 rounded-lg grid place-items-center transition-colors ${locked ? "opacity-40 pointer-events-none" : ""} ${isActive ? "bg-secondary text-secondary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`
            }
          >
            <Icon className="w-5 h-5" strokeWidth={1.75} />
          </NavLink>
          {badgeVal != null ? (
            <span
              data-testid={`nav-badge-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="pointer-events-none absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-primary text-primary-foreground text-[10px] font-bold leading-none ring-2 ring-card shadow-sm"
            >{badgeVal}</span>
          ) : null}
        </div>
      </TooltipTrigger>
      <TooltipContent side="right">{locked ? "Finish your call or go on break to navigate" : `${n.label}${badgeVal != null ? ` · ${badgeVal}` : ""}`}</TooltipContent>
    </Tooltip>
  );
}

function Divider() { return <div className="my-1 w-8 h-px bg-border" />; }
function SectionLabel({ children }) {
  return <div className="text-[9px] uppercase tracking-[0.18em] font-bold text-muted-foreground/60 my-0.5">{children}</div>;
}

function QueueDialog({ open, onOpenChange, queue, onPick }) {
  const tabs = [
    { k: "calls", label: "Calls", count: queue.calls.length, items: queue.calls.map((c) => ({ id: c.id, title: c.customer || c.caller_id, sub: `${c.queue_status} · ${c.queue_time}`, channel: "voice" })) },
    { k: "chats", label: "Chats", count: queue.chats.length, items: queue.chats.map((c) => ({ id: c.id, title: c.contact_name, sub: c.preview, channel: c.primary_channel })) },
    { k: "emails", label: "Emails", count: queue.emails.length, items: queue.emails.map((c) => ({ id: c.id, title: c.contact_name, sub: c.preview, channel: "email" })) },
    { k: "voicemails", label: "Voicemails", count: queue.voicemails.length, items: queue.voicemails.map((c) => ({ id: c.id, title: c.contact_name, sub: c.preview, channel: "voicemail" })) },
  ];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden">
        <DialogHeader className="px-5 pt-5">
          <DialogTitle className="font-display tracking-tight">Live queue</DialogTitle>
          <DialogDescription>Customers, chats and emails currently waiting for an agent.</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="calls" className="px-5 pb-5">
          <TabsList>
            {tabs.map((t) => (
              <TabsTrigger key={t.k} value={t.k} data-testid={`queue-tab-${t.k}`}>
                {t.label}
                <Badge variant="outline" className="ml-2 h-5 min-w-[20px] justify-center px-1 text-[10px]">{t.count}</Badge>
              </TabsTrigger>
            ))}
          </TabsList>
          {tabs.map((t) => (
            <TabsContent key={t.k} value={t.k} className="mt-3 max-h-[60vh] overflow-y-auto">
              {t.items.length === 0 ? (
                <div className="text-center text-sm text-muted-foreground py-8">No {t.label.toLowerCase()} waiting.</div>
              ) : (
                <ul className="space-y-2">
                  {t.items.map((it) => {
                    const ch = channelMeta[it.channel] || channelMeta.system;
                    const Icon = ch.Icon;
                    return (
                      <li key={it.id} className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted/50">
                        <div className={`w-9 h-9 rounded-lg grid place-items-center ${ch.cls}`}><Icon className="w-4 h-4" /></div>
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-sm truncate">{it.title}</div>
                          <div className="text-xs text-muted-foreground truncate">{it.sub}</div>
                        </div>
                        <Button size="sm" data-testid={`queue-pick-${it.id}`} onClick={() => onPick(it)} className="bg-secondary hover:bg-secondary/90 text-secondary-foreground">Pick up</Button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
